import React, { useEffect, useState } from 'react';

import {
  BadFileAttachmentView,
  ChakraBox,
  ChakraFlex,
  ChakraImage,
  ChakraSpinner,
  ChakraText,
  Close,
  EquipUIFireflyV2Theme,
  FileIcons,
} from '@equip.health/ui';

import { S3FileStatus } from '~/lib/constants/s3filestatus.contants';
import {
  getExtension,
  getFileAttachmentErrorMessage,
  getFileIcon,
  isImage,
  removeUUIDFromFilename,
} from '~/lib/util/chat/chat.utils';

const { primary } = EquipUIFireflyV2Theme.colors;

interface FileProps {
  file: File | string;
  fileValidationStatus?: string;
  isLoading?: boolean;
  isShowClose?: boolean;
  isValidFile?: boolean;
  onClick?: () => void;
  onRemove?: () => void;
}
const PreviewImage = ({
  file,
  isLoading = false,
  isShowClose = true,
  onClick,
  onRemove,
}: FileProps): React.ReactElement => {
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return null;
    }

    const objectUrl = URL.createObjectURL(file as File);
    setPreview(objectUrl);

    // free memory when ever this component is unmounted
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return (
    <ChakraBox
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isLoading) onClick?.();
      }}
      position="relative"
    >
      {isLoading && (
        <ChakraFlex
          alignItems="center"
          backgroundColor="#333333"
          border="1px solid #ccc"
          borderRadius="10px"
          bottom="0"
          justifyContent="center"
          left="0"
          opacity="0.6"
          position="absolute"
          right="0"
          top="0"
          zIndex="10"
        >
          <ChakraSpinner color="white" size="lg" />
        </ChakraFlex>
      )}
      {isShowClose && !isLoading && (
        <ChakraFlex
          alignItems="center"
          backgroundColor="#666666"
          border="white 2px solid"
          borderRadius="15px"
          color="white"
          cursor="pointer"
          fontSize="10px"
          height="18px"
          justifyContent="center"
          left="45px"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!isLoading) onRemove?.();
          }}
          paddingLeft="2px"
          paddingRight="2px"
          position="relative"
          top="10px"
          width="18px"
          zIndex="1"
        >
          <Close fill="white" />
        </ChakraFlex>
      )}
      <ChakraImage
        borderRadius="10px"
        height="58px"
        src={preview}
        width="58px"
      />
    </ChakraBox>
  );
};

export const PreviewDocument = ({
  file,
  fileValidationStatus,
  isLoading = false,
  isShowClose = true,
  isValidFile = true,
  onClick,
  onRemove,
}: FileProps): React.ReactElement => {
  const filename = file instanceof File ? file?.name : file;
  const extension = getExtension(filename);

  const getIcon = () => {
    if (fileValidationStatus && fileValidationStatus === S3FileStatus.NEW) {
      return <ChakraSpinner color={primary[100]} size="lg" thickness="3px" />;
    }

    if (fileValidationStatus && !isValidFile) {
      return <BadFileAttachmentView size="lg" />;
    }

    return (
      <FileIcons height="50px" icon={getFileIcon(filename)} width="50px" />
    );
  };

  return (
    <>
      <ChakraBox
        cursor={file instanceof File ? 'default' : 'pointer'}
        gridGap="12px"
        maxWidth="400px"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();

          if (!isLoading) onClick?.();
        }}
        padding="12px"
        position="relative"
        width="90%"
      >
        {isLoading && (
          <ChakraFlex
            alignItems="center"
            backgroundColor="#333333"
            border="1px solid #ccc"
            borderRadius="10px"
            bottom="0"
            justifyContent="center"
            left="0"
            opacity="0.6"
            position="absolute"
            right="0"
            top="0"
            zIndex="10"
          >
            <ChakraSpinner color="white" size="lg" />
          </ChakraFlex>
        )}
        <ChakraFlex
          alignItems="center"
          gridGap="12px"
          justifyContent="space-between"
          width="100%"
        >
          <ChakraBox width="50px">{getIcon()}</ChakraBox>
          <ChakraFlex direction="column" width="80%">
            <ChakraText
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              width="100%"
            >
              {removeUUIDFromFilename(filename)}
            </ChakraText>
            <ChakraText
              color="neutral.secondary"
              textOverflow="ellipsis"
              {...((!fileValidationStatus ||
                fileValidationStatus === 'CLEAN') && {
                textTransform: 'uppercase',
              })}
              wordBreak="break-all"
            >
              {getFileAttachmentErrorMessage(
                isValidFile,
                fileValidationStatus,
                extension,
              )}
            </ChakraText>
          </ChakraFlex>
        </ChakraFlex>
      </ChakraBox>
      {isShowClose && !isLoading && (
        <ChakraFlex
          alignItems="center"
          backgroundColor="#666666"
          border="white 2px solid"
          borderRadius="15px"
          color="white"
          cursor="pointer"
          fontSize="10px"
          height="18px"
          justifyContent="center"
          left="-25px"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!isLoading) onRemove?.();
          }}
          paddingLeft="2px"
          paddingRight="2px"
          position="relative"
          top="-8px"
          width="18px"
          zIndex="1"
        >
          <Close fill="white" />
        </ChakraFlex>
      )}
    </>
  );
};

interface AttachmentViewerProps {
  attachments: File[];
  id: string;
  onClick?: () => void;
  onRemove: (file: File) => void;
  isLoading: boolean;
}

const AttachmentViewer = React.forwardRef<
  HTMLDivElement,
  AttachmentViewerProps
>(
  (
    { attachments, id, onClick, onRemove, isLoading }: AttachmentViewerProps,
    ref?: React.ForwardedRef<HTMLDivElement>,
  ) => {
    return (
      <ChakraFlex gridGap="16px" id={id} padding="12px 0" ref={ref}>
        {(attachments ?? []).map((attachment) => {
          return isImage(attachment.name) ? (
            <PreviewImage
              file={attachment}
              isLoading={isLoading}
              key={attachment.name}
              onClick={onClick}
              onRemove={() => onRemove(attachment)}
            />
          ) : (
            <PreviewDocument
              file={attachment}
              isLoading={isLoading}
              key={attachment.name}
              onClick={onClick}
              onRemove={() => onRemove(attachment)}
            />
          );
        })}
      </ChakraFlex>
    );
  },
);

AttachmentViewer.displayName = 'AttachmentViewer';
export default AttachmentViewer;
